import React, { useState, useRef, useEffect, useCallback } from "react";
import { Network, Coins, Clock, AlertTriangle, BarChart3, User, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { content } from "../data/content";
import SectionHeading from "./SectionHeading";

const ICONS = { network: Network, coins: Coins, clock: Clock, alert: AlertTriangle, chart: BarChart3, user: User, eye: Eye };
const GAP = 20;

const useVisibleCount = () => {
  const [count, setCount] = useState(4);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setCount(w >= 1024 ? 4 : w >= 768 ? 3 : w >= 560 ? 2 : 1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return count;
};

const ChallengeSection = () => {
  const { lang } = useLanguage();
  const t = content[lang].challenge;
  const items = t.items;
  const total = items.length;
  const visible = useVisibleCount();
  const maxIndex = Math.max(0, total - visible);
  const [index, setIndex] = useState(0);
  const viewportRef = useRef(null);
  const [cardW, setCardW] = useState(0);

  useEffect(() => {
    const update = () => {
      const el = viewportRef.current;
      if (!el) return;
      setCardW((el.offsetWidth - (visible - 1) * GAP) / visible);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [visible]);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const go = useCallback(
    (dir) => setIndex((i) => (dir > 0 ? (i >= maxIndex ? 0 : i + 1) : i <= 0 ? maxIndex : i - 1)),
    [maxIndex]
  );

  return (
    <section id="desafio" className="relative overflow-hidden py-24 md:py-28 scroll-mt-24" data-testid="challenge-section">
      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
        <div className="max-w-3xl" data-testid="challenge-heading">
          <SectionHeading kicker={t.kicker} title={t.title} accent={t.titleAccent} body={t.body} testId="challenge" />
        </div>

        <div className="relative mt-12 md:mt-14">
          <button
            onClick={() => go(-1)}
            aria-label="Anterior"
            data-testid="challenge-prev-button"
            className="absolute -left-1 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-narwhal-dark/70 text-white/70 backdrop-blur transition-colors duration-200 hover:border-narwhal-teal/50 hover:text-narwhal-teal md:-left-4"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Siguiente"
            data-testid="challenge-next-button"
            className="absolute -right-1 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-narwhal-dark/70 text-white/70 backdrop-blur transition-colors duration-200 hover:border-narwhal-teal/50 hover:text-narwhal-teal md:-right-4"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>

          <div className="overflow-hidden md:mx-8" ref={viewportRef} data-testid="challenge-cards">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ gap: `${GAP}px`, transform: `translateX(-${index * (cardW + GAP)}px)` }}
            >
              {items.map((item, i) => {
                const Icon = ICONS[item.icon] || Network;
                const active = i >= index && i < index + visible;
                return (
                  <article
                    key={item.title}
                    data-testid={`challenge-card-${i}`}
                    style={{ width: cardW ? `${cardW}px` : "100%" }}
                    className={`flex shrink-0 flex-col rounded-2xl border p-6 transition-[border-color] duration-300 ${
                      i === index ? "border-narwhal-teal/40 bg-narwhal-teal/[0.04]" : "border-white/[0.08] bg-narwhal-dark/40"
                    }`}
                  >
                    <div className="flex items-start gap-3.5">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-narwhal-teal/20 bg-narwhal-teal/[0.06] text-narwhal-teal">
                        <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
                      </span>
                      <h3 className="pt-1 text-base font-semibold leading-snug text-white" data-testid={`challenge-card-title-${i}`}>{item.title}</h3>
                    </div>
                    <p className="mt-5 flex-1 text-sm leading-relaxed text-narwhal-muted" data-testid={`challenge-card-text-${i}`}>{item.text}</p>
                    <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-white/[0.07]">
                      <span className="block h-full w-1/3 rounded-full bg-gradient-to-r from-narwhal-teal to-narwhal-teal/20" />
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/8 pt-10 text-center md:mt-16" data-testid="challenge-closing">
          <p className="text-lg font-light leading-relaxed text-white md:text-xl">{t.closingTitle}</p>
          <p className="mt-1 text-lg font-medium leading-relaxed text-narwhal-teal md:text-xl">{t.closingAccent}</p>
        </div>
      </div>
    </section>
  );
};

export default ChallengeSection;
